import * as React from "react";
import { finishSignupSchema } from "@njoy/common";
import { NormalizedErrorMap } from "@njoy/controller";
import { Form as AntForm } from "antd";
import NewButton from "../../../components/uiMerge/newButton";
import { FormikProps, withFormik, Form } from "formik";
// import { withFormik, FormikProps, Field, Form } from "formik";
import { Page1 } from "./Page1";
import { Page2 } from "./Page2";
import { ToastContainer } from "react-toastify";
import ToastMessage from "../../../hooks/useToast";
import { extractError } from "../../../utils/extractError";


const FormItem = AntForm.Item;

interface FormValues {
  firstname: string;
  lastname: string;
  username: string;
  locationId: string;
  pin: string;
  referralCode: string;
}

interface FinishSignupResult {
  errors?: NormalizedErrorMap | null;
  message?: string | null;
}

interface Props {
  onFinish: () => void;
  submit: (values: FormValues) => Promise<FinishSignupResult | null>;
  step: number;
  nextStep: () => void;
  prevStep: () => void;
}

const PAGE_ONE_FIELDS: Array<keyof FormValues> = [
  "firstname",
  "lastname",
  "username",
  "locationId",
  "referralCode",
];

const PAGE_TWO_FIELDS: Array<keyof FormValues> = ["pin"];

export class C extends React.PureComponent<FormikProps<FormValues> & Props> {
  handleNext = async () => {
    const { validateForm, nextStep, setTouched, values } = this.props;
    await setTouched({
      firstname: true,
      lastname: true,
      username: true,
      locationId: true,
      pin: false,
      referralCode: false
    });
    const errors = await validateForm(values);
    
    // Check if Page 1 fields (excluding PIN) are valid
    const page1HasErrors = !!(errors.firstname || errors.lastname || errors.username || errors.locationId);
    
    if (!page1HasErrors) {
      nextStep();
    }
  };

  render() {
    const { isSubmitting, step, prevStep, values } = this.props;
    return (
      <>
        <ToastContainer />
        <div className="flex flex-col min-h-[78vh] px-[1rem] mb-2">
          <Form className="flex flex-col flex-1">
            <div className="flex-1">
              {step === 1
                ? <Page2 firstname={values.firstname} lastname={values.lastname} />
                : <Page1 />}
            </div>
            <FormItem className="mt-auto">
              <div className="control_buttons_container">
                <div className="control_buttons">
                  {step === 1 ? (
                    <div className="flex flex-row gap-2">
                      <NewButton fullWidth variant="outline" className="ant-btn-primary-outline" onClick={prevStep} type="button">
                        Previous
                      </NewButton>
                      <NewButton fullWidth variant="primary" type="submit" loading={isSubmitting}>
                        Finish
                      </NewButton>
                    </div>
                  ) : (
                    <>
                      {step ? (
                        <>
                          <NewButton fullWidth variant="primary" onClick={prevStep} type="button">
                            Previous
                          </NewButton>
                          <NewButton fullWidth variant="primary" onClick={this.handleNext} type="button">
                            Next
                          </NewButton>
                        </>
                      ) : (
                        <NewButton fullWidth variant="primary" onClick={this.handleNext} type="button">
                          Next
                        </NewButton>
                      )}
                    </>
                  )}
                </div>
              </div>
            </FormItem>
          </Form>
        </div>
      </>
    );
  }
}

export const FinishSignupView = withFormik<Props, FormValues>({
  validationSchema: finishSignupSchema,
  validateOnChange: true,
  validateOnBlur: true,
  mapPropsToValues: () => ({
    firstname: "",
    lastname: "",
    username: "",
    locationId: "",
    pin: "",
    referralCode: ""
  }),
  handleSubmit: async (
    values,
    { props, setErrors, setSubmitting, resetForm, setTouched }
  ) => {
    setSubmitting(true);
    try {
      const result = await props.submit(values);
      setSubmitting(false);

      if (!result || (!result.errors && result.message)) {
        ToastMessage("success", result?.message || "Signup completed successfully!");
        resetForm();
        setTouched({});
        props.onFinish();
        return;
      }

      if (result && result.errors) {
        const errorObj = typeof result.errors === "object" && result.errors !== null ? result.errors : {};
        const errorKeys = Object.keys(errorObj);
        const errorMsg = extractError(errorObj);
        ToastMessage("error", typeof errorMsg === "string" ? errorMsg : "Signup failed. Please check your input.");
        setErrors(result.errors as any); // If result.errors is not FormikErrors, cast to any
        setTouched(
          errorKeys.reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {} as Record<string, boolean>
          )
        );

        if (props.step === 1 && errorKeys.some((key) => PAGE_ONE_FIELDS.includes(key as keyof FormValues))) {
          props.prevStep();
        }

        if (props.step === 0 && errorKeys.some((key) => PAGE_TWO_FIELDS.includes(key as keyof FormValues))) {
          props.nextStep();
        }
      }
    } catch (err) {
      setSubmitting(false);
      if ((err as Error)?.name === "AbortError") {
        ToastMessage("error", "Request was aborted. Please try again.");
      } else {
        ToastMessage("error", "Something went wrong. Please try again.");
      }
    }
  },
})(C);